import React, { useState, useEffect } from 'react';
import axios from 'axios';
import GetCookie from '../../utils/GetCookie';

const CopilotMigrateModal = ({
  isOpen,
  onClose,
  onSuccess,
  onError,
  fabricId,
  fileS3Path,
  fileName,
}) => {
  const [selectedThreadId, setSelectedThreadId] = useState(null);
  const [threads, setThreads] = useState([]);
  const [loading, setLoading] = useState(false);
  const [migrating, setMigrating] = useState(false);
  const [searchInput, setSearchInput] = useState('');
  const [page, setPage] = useState(1);
  const [size] = useState(25);
  const [total, setTotal] = useState(0);

  const resetState = () => {
    setSelectedThreadId(null);
    setSearchInput('');
    setThreads([]);
    setPage(1);
    setTotal(0);
  };

  useEffect(() => {
    if (isOpen && fabricId) {
      fetchThreads();
    }
  }, [isOpen, fabricId, page]);

  const fetchThreads = async () => {
    setLoading(true);
    try {
      const response = await axios.post(
        '/pceapi/copilot/fabric_unstructured_data_tree',
        {
          include_documents: true,
          page: 1,
          size: 100,
          fabric_id: fabricId,
        },
        { headers: { Authorization: GetCookie('access_token') } }
      );

      const data = response.data;
      if (data.status === 'success' || (data.data && data.data.length > 0)) {
        const allThreads = [];
        (data.data || []).forEach(fabric => {
          (fabric.threads || []).forEach(thread => {
            allThreads.push({
              id: thread.id,
              name: thread.name,
              thread_access_level: thread.thread_access_level || 'PRIVATE',
              document_count: (thread.documents || []).length,
              tags: thread.tags || [],
              user_access: thread.user_access || [],
            });
          });
        });
        setThreads(allThreads);
        setTotal(allThreads.length);
      } else {
        setThreads([]);
        setTotal(0);
      }
    } catch (error) {
      console.error('Error fetching threads for migrate:', error);
      setThreads([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  };

  const filteredThreads = threads.filter(t =>
    !searchInput || t.name.toLowerCase().includes(searchInput.toLowerCase())
  );

  const totalPages = Math.ceil(filteredThreads.length / size) || 1;
  const paginatedThreads = filteredThreads.slice((page - 1) * size, page * size);

  const handleMigrate = async () => {
    if (!selectedThreadId) {
      if (onError) onError('Please select a Data Collection');
      return;
    }

    const threadData = threads.find(t => t.id === selectedThreadId);
    if (!threadData) {
      if (onError) onError('Selected Data Collection not found');
      return;
    }

    setMigrating(true);

    try {
      const presignedResp = await axios.post('/pceapi/copilot/model-presigned-url', {
        s3_path: fileS3Path
      }, {
        headers: {
          'Content-Type': 'application/json',
          'Authorization': GetCookie('access_token'),
        }
      });

      if (presignedResp.data.status !== 'success' || !presignedResp.data.preview_url) {
        throw new Error('Failed to get download URL');
      }

      const downloadResp = await fetch(presignedResp.data.preview_url);
      if (!downloadResp.ok) throw new Error('Failed to download file');

      const blob = await downloadResp.blob();
      const file = new File([blob], fileName || 'model.xlsx', {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      });

      const formData = new FormData();
      formData.append('thread_id', selectedThreadId);
      formData.append('name', threadData.name || 'Untitled');
      formData.append('thread_access_level', threadData.thread_access_level || 'PRIVATE');
      formData.append('tags', JSON.stringify(threadData.tags || []));
      formData.append('user_access', JSON.stringify(
        (threadData.user_access || []).map(u => u.user_id || u)
      ));
      formData.append('excelFiles', file);

      const response = await axios.post(
        '/pceapi/threads/unstructured/update',
        formData,
        {
          headers: {
            Authorization: GetCookie('access_token'),
            'Content-Type': 'multipart/form-data'
          }
        }
      );

      if (response.data.status === 'success') {
        resetState();
        onClose();
        if (onSuccess) onSuccess('Excel migrated to Data Collection successfully');
      } else {
        if (onError) onError(response.data.message || 'Failed to migrate Excel');
      }
    } catch (error) {
      console.error('Error migrating Excel:', error);
      if (onError) onError(error.response?.data?.message || error.message || 'Failed to migrate Excel');
    } finally {
      setMigrating(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="modal d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.4)' }}>
      <div className="modal-dialog modal-dialog-centered" style={{ maxWidth: 'var(--px-500)' }}>
        <div className="modal-content ev-card-3 ev-padding-small px-3 pt-3 pb-3">
          {/* Header */}
          <div className="d-flex justify-content-between align-items-center mb-2">
            <span className="ev-bold-med ev-reg-p-blue ev-fs-14">
              Migrate Generated Model to Data Collection
            </span>
            <button
              type="button"
              className="btn-close"
              onClick={() => { resetState(); onClose(); }}
              aria-label="Close"
            ></button>
          </div>

          {/* Search and Pagination Row */}
          <div className="d-flex justify-content-between align-items-center margin-bottom-2">
            <div className="col-5">
              <div className="position-relative">
                <input
                  type="text"
                  className="form-control search-input ms-0"
                  placeholder="Search Data Collections"
                  value={searchInput}
                  onChange={(e) => { setSearchInput(e.target.value); setPage(1); }}
                />
                <button
                  type="button"
                  className="ev-btn-lightblue-fill-round search-bar-button"
                  disabled
                >
                  <i className="bi bi-search"></i>
                </button>
              </div>
            </div>

            <div className="col-7 d-flex justify-content-end align-items-center">
              <div className="d-flex align-items-center">
                <button
                  type="button"
                  className="ev-btn-light-fill ev-btn-xsmall"
                  disabled={page === 1}
                  onClick={() => setPage(page - 1)}
                >
                  <i className="bi bi-chevron-left"></i>
                </button>
                <span className="ev-xsmall-p-dark mx-2">
                  Page {page}/{totalPages}
                </span>
                <button
                  type="button"
                  className="ev-btn-light-fill ev-btn-xsmall"
                  disabled={page >= totalPages}
                  onClick={() => setPage(page + 1)}
                >
                  <i className="bi bi-chevron-right"></i>
                </button>
              </div>
            </div>
          </div>

          {/* Thread List */}
          <div className="scrollable-element vh-25 margin-bottom">
            {loading ? (
              <div className="text-center py-2">
                <div className="spinner-border text-primary" role="status">
                  <span className="visually-hidden">Loading...</span>
                </div>
              </div>
            ) : paginatedThreads.length === 0 ? (
              <p className="ev-small-p-blue text-center mt-2">No Data Collections found</p>
            ) : (
              <div className="thread-tree ms-1">
                {paginatedThreads.map((thread) => (
                  <div key={thread.id} className="margin-bottom-1">
                    <div className="d-flex align-items-center">
                      <div className="form-check mb-0">
                        <input
                          className="form-check-input"
                          type="radio"
                          id={`copilot-migrate-thread-${thread.id}`}
                          name="copilotMigrateThread"
                          checked={selectedThreadId === thread.id}
                          onChange={() => setSelectedThreadId(thread.id)}
                        />
                        <label className="form-check-label" htmlFor={`copilot-migrate-thread-${thread.id}`}>
                          <span className="ev-small-p-blue">{thread.name}</span> &nbsp;
                          <span className={`badge ev-btn-small ${thread.thread_access_level === 'PUBLIC' ? 'bg-primary' : 'bg-danger'}`}>
                            {thread.thread_access_level || 'PRIVATE'}
                          </span>
                          <span className="badge bg-secondary ms-1 ev-btn-xsmall">
                            {thread.document_count || 0} docs
                          </span>
                        </label>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Action Buttons */}
          <div className="d-flex justify-content-end gap-2">
            <button
              type="button"
              className="ev-btn-blue-fill-round ev-btn-small ev-bold-reg"
              onClick={handleMigrate}
              disabled={!selectedThreadId || migrating}
            >
              {migrating ? (
                <span className="spinner-border spinner-border-sm me-1" role="status"></span>
              ) : (
                <i className="bi bi-arrow-right-circle"></i>
              )} Migrate
            </button>
            <button
              type="button"
              className="ev-btn-blue-outline-round ev-btn-small"
              onClick={() => { resetState(); onClose(); }}
            >
              Cancel
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CopilotMigrateModal;
